import axios from 'axios'
import { FilterQuery, FilterQueryBase, ListAccountInfoResponseDto, ListInvoiceResponseDto } from '../definition'
import {
  AdminGetCustomerBookingsFilterQuery,
  AdminGetTaskByAccountIdFilterQuery,
  BookingDto,
  CheckCancelBookingDto,
  ConfigCutoffDurationDto,
  ConfigPaymentTimeframeDto,
  ContractBookingListFilterQuery,
  CreateBlockOffOnceBody,
  CreateBlockOffRecurringBody,
  ListBookingResponseDto,
  ListSubBookingResponseDto,
  ListTaskResponseDto,
  NewBookingDto,
  NewRecurring2BookingDto,
  NewRecurringBookingDto,
  RefundPolicy,
  StaticDataRequestBodyDto,
  StaticDataResponseDto,
  TaskFilterQuery,
  UpdateRecurringBookingDto,
  UpdateTaskAppointmentDto
} from '../definition/booking.dto'
import { GetTimeSlotByDateResponseDto, TimeSlotDurationDto } from '../definition/time-slot.dto'
import { ENV } from '../initialization'

const DOMAIN = ENV.BACKEND_PREFIX_URL

export const createBooking = async (data: NewBookingDto): Promise<BookingDto | undefined> => {
  const url = `${DOMAIN}/api/booking/booking/create/one-time`
  const response = await axios.post(url, data)
  return response.data
}

export const createRecurringBooking = async (data: NewRecurringBookingDto): Promise<void> => {
  const url = `${DOMAIN}/api/booking/booking/admin/create/recurring`
  await axios.post(url, data)
}

export const createRecurring2Booking = async (data: NewRecurring2BookingDto): Promise<void> => {
  const url = `${DOMAIN}/api/booking/booking/create/recurring2`
  await axios.post(url, data)
}

export const updateRecurring2Booking = async (id: string,data: NewRecurring2BookingDto): Promise<void> => {
  const url = `${DOMAIN}/api/booking/booking/update/recurring2/${id}`
  await axios.post(url, data)
}

export const endRecurring2Booking = async (id: string, scheduledToEnd?: string): Promise<void> => {
  const url = `${DOMAIN}/api/booking/booking/end/recurring2/${id}`
  await axios.post(url, { scheduledToEnd })
}

export const cancelEndRecurring2Booking = async (id: string): Promise<void> => {
  const url = `${DOMAIN}/api/booking/booking/cancel-scheduled-end/recurring2/${id}`
  await axios.post(url)
}

export const updateRecurringBooking = async (data: UpdateRecurringBookingDto): Promise<BookingDto> => {
  const url = `${DOMAIN}/api/booking/booking/admin/update/recurring`
  return await (
    await axios.put(url, data)
  ).data
}

export const asyncGetContractBookings = async (
  params: ContractBookingListFilterQuery
): Promise<ListBookingResponseDto> => {
  const url = `${DOMAIN}/api/booking/booking/contract/list`
  const response = await axios.get<ListBookingResponseDto>(url, { params })
  return response.data
}

export const asyncGetScheduleBookings = async (
  params: ContractBookingListFilterQuery
): Promise<ListBookingResponseDto> => {
  const url = `${DOMAIN}/api/booking/booking/list`
  const response = await axios.get<ListBookingResponseDto>(url, { params })
  return response.data
}

export const asyncBookingDetail = async (id: string): Promise<BookingDto> => {
  const url = `${DOMAIN}/api/booking/booking/detail/${id}`
  const response = await axios.get<BookingDto>(url)
  return response.data
}

export const asyncGetSubBooking = async (id: string, params: ContractBookingListFilterQuery): Promise<ListSubBookingResponseDto> => {
  const url = `${DOMAIN}/api/booking/booking/recurring2/${id}/sub-bookings`
  const response = await axios.get<ListSubBookingResponseDto>(url, { params })
  return response.data
}

export const asyncGetTaskSchedule = async (params: TaskFilterQuery) => {
  const url = `${DOMAIN}/api/booking/task/admin/task-list`
  const response = await axios.get<ListTaskResponseDto>(url, { params })
  return response.data
}

export const asyncGetStaticData = async () => {
  const url = `${DOMAIN}/api/booking/get-static-data`
  const response = await axios.get<StaticDataResponseDto>(url)
  return response.data
}

export const asyncSetStaticData = async (data: StaticDataRequestBodyDto) => {
  const url = `${DOMAIN}/api/booking/set-static-data`
  const response = await axios.put<StaticDataResponseDto>(url, data)
  return response.data
}

export const asyncUpdateTaskAppointment = async (data: UpdateTaskAppointmentDto) => {
  const url = `${DOMAIN}/api/booking/task/appointment/update`
  await axios.put<UpdateTaskAppointmentDto>(url, data)
}

export const asyncGetNewBookingOnDashboard = async (params: {
  offset?: number
  limit?: number
  assigned?: boolean
}) => {
  const url = `${DOMAIN}/api/booking/report/booking-dashboard`
  return await axios.get<ListBookingResponseDto>(url, { params })
}

export const asyncGetCreatedBookingInRangeTime = async (params: {
  fromDate: Date
  toDate: Date
  serviceId?: string
}) => {
  const url = `${DOMAIN}/api/booking/report/created-booking`
  return await axios.get<ListBookingResponseDto>(url, { params })
}

export const asyncGetCompletedTaskOnDashboard = async (params: { fromDate: Date; toDate: Date }) => {
  const url = `${DOMAIN}/api/booking/report/completed-task`
  return await axios.get<ListTaskResponseDto>(url, { params })
}

export const asyncGetNewCustomerOnDashboard = async (params: { fromDate: Date; toDate: Date }) => {
  const url = `${DOMAIN}/api/booking/report/created-customer`
  return await axios.get<ListAccountInfoResponseDto>(url, { params })
}

export const asyncGetVerifiedInvoiceOnDashboard = async (params: { fromDate: Date; toDate: Date }) => {
  const url = `${DOMAIN}/api/booking/report/verified-invoice`
  return await axios.get<ListInvoiceResponseDto>(url, { params })
}

export const asyncGetCreatedInvoiceOnDashboard = async (params: { fromDate: Date; toDate: Date }) => {
  const url = `${DOMAIN}/api/booking/report/created-invoice`
  return await axios.get<ListInvoiceResponseDto>(url, { params })
}

export const asyncAdminGetCustomerBooking = async (
  params: AdminGetCustomerBookingsFilterQuery
): Promise<ListBookingResponseDto> => {
  const url = `${DOMAIN}/api/booking/booking/admin/customer-booking`
  const res = await axios.get<ListBookingResponseDto>(url, { params })
  return res.data
}

export const asyncGetTimeSlotsByDate = async (date: Date): Promise<GetTimeSlotByDateResponseDto> => {
  const url = `${DOMAIN}/api/booking/booking/time-slot-by-date`
  const res = await axios.get<GetTimeSlotByDateResponseDto>(url, {
    params: { date }
  })
  return res.data
}

export const asyncApproveQuote = async (params: { quoteId: string }) => {
  const url = `${DOMAIN}/api/booking/booking/quote/accept`
  const res = await axios.post(url, params)
  return res.data
}

export const asyncAdminPlanAppoinment = async (params: { bookingId: string }) => {
  const url = `${DOMAIN}/api/booking/task/admin-plan-sessions`
  const res = await axios.put(url, params)
  return res.data
}

export const asyncCancelBooking = async (id: string) => {
  const url = `${DOMAIN}/api/booking/booking/admin/cancel/${id}`
  const response = await axios.put(url)
  return response.data
}

export const asyncDeleteBooking = async (bookingId: string) => {
  const url = `${DOMAIN}/api/booking/booking/admin/delete-bookings`
  const response = await axios.post(url, { bookingIds: [bookingId] })
  return response.data
}

export const asyncSendBooking = async (id: string, image: any): Promise<void> => {
  const url = `${DOMAIN}/api/booking/booking/send-details/${id}`
  let bodyFormData = new FormData()

  bodyFormData.append('attachFile', image)
  await axios({
    method: 'post',
    url: url,
    data: bodyFormData,
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const asyncGetCutoffDuration = async () => {
  const url = `${DOMAIN}/api/booking/app-config/cut-off-duration`
  const response = await axios.get<ConfigCutoffDurationDto>(url)
  return response.data
}

export const asyncSaveCutoffDuration = async (data: ConfigCutoffDurationDto) => {
  const url = `${DOMAIN}/api/booking/app-config/cut-off-duration`
  const res = await axios.put<ConfigCutoffDurationDto>(url, data)
  return res.data
}

export const asyncGetPaymentTimeframe = async () => {
  const url = `${DOMAIN}/api/booking/app-config/payment-timeframe`
  const response = await axios.get<ConfigPaymentTimeframeDto>(url)
  return response.data
}

export const asyncSavePaymentTimeframe = async (data: ConfigPaymentTimeframeDto) => {
  const url = `${DOMAIN}/api/booking/app-config/payment-timeframe`
  const res = await axios.put<ConfigPaymentTimeframeDto>(url, data)
  return res.data
}

export const asyncAdminGetTaskByAccountId = async (params: AdminGetTaskByAccountIdFilterQuery) => {
  const url = `${DOMAIN}/api/booking/task/admin/task-list/tech`
  const response = await axios.get<ListTaskResponseDto>(url, { params })
  return response.data
}

export const asyncCreateBlockoffOnce = async (data: CreateBlockOffOnceBody) => {
  const url = `${DOMAIN}/api/booking/block-off-employee`
  const res = await axios.post<ConfigPaymentTimeframeDto>(url, data)
  return res.data
}

export const asyncCreateBlockoffRecurring = async (data: CreateBlockOffRecurringBody) => {
  const url = `${DOMAIN}/api/booking/block-off-employee`
  const res = await axios.post<ConfigPaymentTimeframeDto>(url, data)
  return res.data
}

export const asyncDeleteBlockOff = async (id: string, date?: string) => {
  const url = `${DOMAIN}/api/booking/block-off-employee/${id}${date ? `?date=${date}` : ''}`
  const response = await axios.delete(url)
  return response.data
}

export const asyncGetServiceAvailability = async (serviceId: string, date: string): Promise<TimeSlotDurationDto[]> => {
  const url = `${DOMAIN}/api/booking/booking/get-vendor-availability`
  const response = await axios.get(url, {
    params: { serviceId, date }
  })
  return response.data
}

export const asyncCheckBookingCancel = async (id: string): Promise<CheckCancelBookingDto> => {
  const url = `${DOMAIN}/api/booking/booking/check-cancel/${id}`
  const response = await axios.get(url)
  return response.data
}

export const asyncGetRefundPolicy = async (): Promise<RefundPolicy> => {
  const url = `${DOMAIN}/api/booking/app-config/cancel-booking-config`
  const response = await axios.get(url)
  return response.data
}

export const asyncConfirmRefund = async (bookingId: string, taskIds: string[], cancelReason: string): Promise<any> => {
  const url = `${DOMAIN}/api/booking/booking/cancel/${bookingId}`
  const response = await axios.post(url, { taskIds, cancelReason })
  return response.data
}
